import { Link } from "react-router-dom";
import { Download, Infinity as InfinityIcon } from "lucide-react";
import { useEntitlements } from "../../hooks/useEntitlements";

const PLAN_LABEL: Record<string, string> = {
  FREE: "Free",
  BASIC: "Basic",
  PREMIUM: "Premium",
  PLATINUM: "Platinum",
  ULTRA_PRO: "Ultra Pro",
};

const PLAN_STYLE: Record<string, string> = {
  FREE: "bg-gray-100 text-gray-600 hover:bg-gray-200",
  BASIC: "bg-sky-50 text-sky-700 hover:bg-sky-100",
  PREMIUM: "bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
  PLATINUM: "bg-violet-50 text-violet-700 hover:bg-violet-100",
  ULTRA_PRO:
    "bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:opacity-90",
};

/**
 * Header chip showing the current plan and remaining download credits.
 *
 * Doubles as the entry point to pricing, so the upgrade path is always one
 * click away from anywhere in the app.
 */
export default function PlanCreditBadge({
  className = "",
}: {
  className?: string;
}) {
  const { entitlements, isLoading, planTier, downloadCredits } =
    useEntitlements();

  // Nothing useful to show until the plan resolves.
  if (isLoading || !entitlements) return null;

  const isUnlimited = downloadCredits === -1;
  const isOutOfCredits = !isUnlimited && downloadCredits < 1;

  return (
    <Link
      to="/pricing"
      title={
        isUnlimited
          ? "Unlimited downloads"
          : `${downloadCredits} download credit${downloadCredits === 1 ? "" : "s"} remaining`
      }
      className={`hidden items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-bold transition-all sm:inline-flex ${
        PLAN_STYLE[planTier] ?? PLAN_STYLE.FREE
      } ${className}`}
    >
      <span className="uppercase tracking-wider">
        {PLAN_LABEL[planTier] ?? "Free"}
      </span>

      <span
        className={`flex items-center gap-1 border-l pl-2 ${
          planTier === "ULTRA_PRO" ? "border-white/30" : "border-current/20"
        } ${isOutOfCredits ? "text-amber-600" : ""}`}
      >
        {isUnlimited ? (
          <InfinityIcon className="h-3.5 w-3.5" />
        ) : (
          <>
            <Download className="h-3.5 w-3.5" />
            {downloadCredits}
          </>
        )}
      </span>
    </Link>
  );
}
